package org.moxultimate.topwithmox.productcrud.main

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.last
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.product.api.ProductRepository
import org.moxultimate.topwithmox.utils.toTitleCase
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.product.data.ProductResponse
import org.moxultimate.topwithmox.productcrud.api.ProductCRUDRepository
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * ProductCRUDViewModel
 * Manages cursor-based pagination, client-side type/search filtering,
 * and create/update/delete operations for product content entries.
 *
 * @param repository Read-only data source for product content, tags, and types.
 * @param crudRepository Write repository for create, update, delete, saveTag, and saveType.
 */
class ProductCRUDViewModel(
    private val repository: ProductRepository,
    private val crudRepository: ProductCRUDRepository,
) : ViewModel() {

    // ── Content operation state (create / update / delete content) ───────────

    private val _crudState = MutableStateFlow<ProductCRUDState>(ProductCRUDState.Idle)
    val crudState: StateFlow<ProductCRUDState> = _crudState

    // ── ID of the most-recently created content (non-null after a successful create) ──

    private val _createdContentId = MutableStateFlow<Long?>(null)
    val createdContentId: StateFlow<Long?> = _createdContentId

    /**
     * resetCreatedContentId
     * Clears the captured content ID — call when navigating away from the add page.
     */
    fun resetCreatedContentId() { _createdContentId.value = null }

    // ── Product item operation state (create / update / delete product) ───────

    private val _productOpState = MutableStateFlow<ProductCRUDState>(ProductCRUDState.Idle)
    val productOpState: StateFlow<ProductCRUDState> = _productOpState

    // ── Full content with products (loaded by ID for the update page) ─────────

    private val _contentProducts = MutableStateFlow<List<ProductResponse>>(emptyList())
    val contentProducts: StateFlow<List<ProductResponse>> = _contentProducts

    private val _isLoadingProducts = MutableStateFlow(false)
    val isLoadingProducts: StateFlow<Boolean> = _isLoadingProducts

    // ── List state ────────────────────────────────────────────────────────────

    private val _listState = MutableStateFlow<ProductContentListState>(ProductContentListState.Loading)
    val listState: StateFlow<ProductContentListState> = _listState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMoreInCurrentView = MutableStateFlow(true)
    val hasMoreInCurrentView: StateFlow<Boolean> = _hasMoreInCurrentView

    // ── Accumulated contents (no active filter makes an API call) ─────────────

    private val accumulatedContents = mutableListOf<ProductContentResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMore = true

    // ── Filter state ──────────────────────────────────────────────────────────

    private val _allTypes = MutableStateFlow<List<String>>(emptyList())
    val allTypes: StateFlow<List<String>> = _allTypes

    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    // ── Load list ─────────────────────────────────────────────────────────────

    /**
     * loadContents
     * Resets accumulated state and fetches the first page of product content.
     * Call from LaunchedEffect(Unit).
     */
    fun loadContents() {
        accumulatedContents.clear()
        lastId = Long.MAX_VALUE
        hasMore = true
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _hasMoreInCurrentView.value = true
        fetchPage(isInitialLoad = true)
    }

    /**
     * loadMoreIfNeeded
     * Called by the scroll handler when the user nears the bottom.
     * No-op when already loading or no more pages exist.
     */
    fun loadMoreIfNeeded() {
        if (hasMore && !_isLoadingMore.value) fetchPage(isInitialLoad = false)
    }

    private fun fetchPage(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getProductContents(lastId).collect { state ->
                _listState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) ProductContentListState.Loading else _listState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        ProductContentListState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        val page = state.data
                        _isLoadingMore.value = false
                        if (page.size < 20) hasMore = false
                        accumulatedContents.addAll(page)
                        if (page.isNotEmpty()) lastId = page.last().id
                        _hasMoreInCurrentView.value = hasMore
                        buildListState()
                    }
                }
            }
        }
    }

    // ── Filtering ─────────────────────────────────────────────────────────────

    /**
     * toggleType
     * Adds or removes [type] from the active type filter and rebuilds state client-side.
     *
     * @param type Type string matching a value from [allTypes].
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        _listState.value = buildListState()
    }

    /**
     * setSearchText
     * Updates the live search text and immediately re-filters the loaded content list.
     *
     * @param text Text to search for in content title, description, and tags.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _listState.value = buildListState()
    }

    /**
     * clearAllFilters
     * Resets all active type chips and search text, restoring the full content list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _listState.value = buildListState()
    }

    /**
     * buildListState
     * Applies active type chips and search text as client-side filters over accumulatedContents.
     * Type filter: content tags must contain any selected type (OR logic).
     * Search filter: title, description, or tags must contain the search text.
     */
    private fun buildListState(): ProductContentListState.Success {
        val types = _selectedTypes.value
        val text = _searchText.value.trim()
        val result = accumulatedContents.filter { content ->
            val matchesType = types.isEmpty() ||
                    types.any { type -> content.tags?.contains(type.trim(), ignoreCase = true) == true }
            val matchesText = text.isBlank() ||
                    content.title.contains(text, ignoreCase = true) ||
                    content.description?.contains(text, ignoreCase = true) == true ||
                    content.tags?.contains(text, ignoreCase = true) == true
            matchesType && matchesText
        }
        return ProductContentListState.Success(contents = result, hasMore = hasMore)
    }

    // ── Tags / Types loading ──────────────────────────────────────────────────

    /**
     * loadAllTypes
     * Fetches all distinct content types from the API. Call once on page load.
     */
    fun loadAllTypes() {
        viewModelScope.launch {
            repository.getAllTypes().collect { state ->
                if (state is APIState.Success) _allTypes.value = state.data.distinct().sorted()
            }
        }
    }

    /**
     * loadAllTags
     * Fetches all tags from the API. Call once on page load.
     */
    fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    // ── CRUD operations ───────────────────────────────────────────────────────

    /**
     * createContent
     * Creates a new product content entry via POST /product-contents.
     * Also persists new tags and type to their respective lookup tables.
     *
     * @param entry Form data for the new content.
     */
    fun createContent(entry: ProductContentFormEntry) {
        viewModelScope.launch {
            _crudState.value = ProductCRUDState.Loading
            try {
                val imageBytes = entry.thumbnailFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.createProductContent(
                    title       = entry.title,
                    description = entry.description.takeIf { it.isNotBlank() },
                    videoLink   = entry.videoLink.takeIf { it.isNotBlank() },
                    tags        = entry.tags.takeIf { it.isNotBlank() },
                    type        = entry.type.takeIf { it.isNotBlank() },
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = ProductCRUDState.Error(result.error, result.statusCode)
                    is APIState.Success -> {
                        persistTagsAndType(entry)
                        _createdContentId.value = result.data.id
                        _crudState.value = ProductCRUDState.Success("Successfully created!")
                    }
                    else -> Unit
                }
            } catch (e: Throwable) {
                _crudState.value = ProductCRUDState.Error(e.message ?: "Create failed")
            }
        }
    }

    /**
     * updateContent
     * Updates an existing product content entry via PUT /product-contents/{id}.
     * Also persists any new tags and type to their respective lookup tables.
     *
     * @param id Content ID to update.
     * @param entry Updated form data.
     */
    fun updateContent(id: Long, entry: ProductContentFormEntry) {
        viewModelScope.launch {
            _crudState.value = ProductCRUDState.Loading
            try {
                val imageBytes = entry.thumbnailFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.updateProductContent(
                    id          = id,
                    title       = entry.title.takeIf { it.isNotBlank() },
                    description = entry.description.takeIf { it.isNotBlank() },
                    videoLink   = entry.videoLink.takeIf { it.isNotBlank() },
                    tags        = entry.tags.takeIf { it.isNotBlank() },
                    type        = entry.type.takeIf { it.isNotBlank() },
                    imageBytes  = imageBytes,
                ).last()) {
                    is APIState.Error -> _crudState.value = ProductCRUDState.Error(result.error, result.statusCode)
                    else -> {
                        persistTagsAndType(entry)
                        _crudState.value = ProductCRUDState.Success("Successfully updated!")
                    }
                }
            } catch (e: Throwable) {
                _crudState.value = ProductCRUDState.Error(e.message ?: "Update failed")
            }
        }
    }

    /**
     * deleteContent
     * Deletes a product content entry via DELETE /product-contents/{id}.
     *
     * @param id Content ID to delete.
     */
    fun deleteContent(id: Long) {
        viewModelScope.launch {
            _crudState.value = ProductCRUDState.Loading
            try {
                when (val result = crudRepository.deleteProductContent(id).last()) {
                    is APIState.Error -> _crudState.value = ProductCRUDState.Error(result.error, result.statusCode)
                    else -> _crudState.value = ProductCRUDState.Success("Successfully deleted!")
                }
            } catch (e: Throwable) {
                _crudState.value = ProductCRUDState.Error(e.message ?: "Delete failed")
            }
        }
    }

    /**
     * resetState
     * Resets the content CRUD operation state back to Idle.
     */
    fun resetState() {
        _crudState.value = ProductCRUDState.Idle
    }

    /**
     * resetProductState
     * Resets the product item CRUD operation state back to Idle.
     */
    fun resetProductState() {
        _productOpState.value = ProductCRUDState.Idle
    }

    // ── Product item loading ──────────────────────────────────────────────────

    /**
     * loadContentById
     * Fetches a single product content with its full product list from GET /product-contents/{id}.
     * Populates [contentProducts] so the update page can display them.
     *
     * @param id The content ID to load.
     */
    fun loadContentById(id: Long) {
        viewModelScope.launch {
            _isLoadingProducts.value = true
            repository.getProductContentById(id).collect { state ->
                when (state) {
                    is APIState.Loading -> {}
                    is APIState.Error -> _isLoadingProducts.value = false
                    is APIState.Success -> {
                        _isLoadingProducts.value = false
                        _contentProducts.value = state.data.products
                    }
                }
            }
        }
    }

    // ── Product item CRUD ─────────────────────────────────────────────────────

    /**
     * createProductItem
     * Creates a new product entry under [contentId] via POST /products.
     *
     * @param contentId Parent content ID.
     * @param entry Form data for the new product.
     */
    fun createProductItem(contentId: Long, entry: ProductFormEntry) {
        viewModelScope.launch {
            _productOpState.value = ProductCRUDState.Loading
            try {
                val imageBytes = entry.imageFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.createProduct(
                    contentId    = contentId,
                    brand        = entry.brand,
                    name         = entry.name,
                    label        = entry.label.takeIf { it.isNotBlank() },
                    description  = entry.description,
                    pros         = entry.pros.takeIf { it.isNotBlank() },
                    cons         = entry.cons.takeIf { it.isNotBlank() },
                    shopeeLink   = entry.shopeeLink.takeIf { it.isNotBlank() },
                    shopeePrice  = entry.shopeePrice.takeIf { it.isNotBlank() },
                    shopeePromo  = entry.shopeePromo.takeIf { it.isNotBlank() },
                    lazadaLink   = entry.lazadaLink.takeIf { it.isNotBlank() },
                    lazadaPrice  = entry.lazadaPrice.takeIf { it.isNotBlank() },
                    lazadaPromo  = entry.lazadaPromo.takeIf { it.isNotBlank() },
                    amazonLink   = entry.amazonLink.takeIf { it.isNotBlank() },
                    amazonPrice  = entry.amazonPrice.takeIf { it.isNotBlank() },
                    amazonPromo  = entry.amazonPromo.takeIf { it.isNotBlank() },
                    tiktokLink   = entry.tiktokLink.takeIf { it.isNotBlank() },
                    tiktokPrice  = entry.tiktokPrice.takeIf { it.isNotBlank() },
                    tiktokPromo  = entry.tiktokPromo.takeIf { it.isNotBlank() },
                    imageBytes   = imageBytes,
                ).last()) {
                    is APIState.Error -> _productOpState.value = ProductCRUDState.Error(result.error, result.statusCode)
                    else -> _productOpState.value = ProductCRUDState.Success("Product created successfully!")
                }
            } catch (e: Throwable) {
                _productOpState.value = ProductCRUDState.Error(e.message ?: "Create failed")
            }
        }
    }

    /**
     * updateProductItem
     * Updates an existing product entry via PUT /products/{id}.
     *
     * @param id Product ID to update.
     * @param entry Updated form data.
     */
    fun updateProductItem(id: Long, entry: ProductFormEntry) {
        viewModelScope.launch {
            _productOpState.value = ProductCRUDState.Loading
            try {
                val imageBytes = entry.imageFile?.let { readFileAsBytes(it) }
                when (val result = crudRepository.updateProduct(
                    id           = id,
                    brand        = entry.brand.takeIf { it.isNotBlank() },
                    name         = entry.name.takeIf { it.isNotBlank() },
                    label        = entry.label.takeIf { it.isNotBlank() },
                    description  = entry.description.takeIf { it.isNotBlank() },
                    pros         = entry.pros.takeIf { it.isNotBlank() },
                    cons         = entry.cons.takeIf { it.isNotBlank() },
                    shopeeLink   = entry.shopeeLink.takeIf { it.isNotBlank() },
                    shopeePrice  = entry.shopeePrice.takeIf { it.isNotBlank() },
                    shopeePromo  = entry.shopeePromo.takeIf { it.isNotBlank() },
                    lazadaLink   = entry.lazadaLink.takeIf { it.isNotBlank() },
                    lazadaPrice  = entry.lazadaPrice.takeIf { it.isNotBlank() },
                    lazadaPromo  = entry.lazadaPromo.takeIf { it.isNotBlank() },
                    amazonLink   = entry.amazonLink.takeIf { it.isNotBlank() },
                    amazonPrice  = entry.amazonPrice.takeIf { it.isNotBlank() },
                    amazonPromo  = entry.amazonPromo.takeIf { it.isNotBlank() },
                    tiktokLink   = entry.tiktokLink.takeIf { it.isNotBlank() },
                    tiktokPrice  = entry.tiktokPrice.takeIf { it.isNotBlank() },
                    tiktokPromo  = entry.tiktokPromo.takeIf { it.isNotBlank() },
                    imageBytes   = imageBytes,
                ).last()) {
                    is APIState.Error -> _productOpState.value = ProductCRUDState.Error(result.error, result.statusCode)
                    else -> _productOpState.value = ProductCRUDState.Success("Product updated successfully!")
                }
            } catch (e: Throwable) {
                _productOpState.value = ProductCRUDState.Error(e.message ?: "Update failed")
            }
        }
    }

    /**
     * deleteProductItem
     * Deletes a product entry via DELETE /products/{id}.
     *
     * @param id Product ID to delete.
     */
    fun deleteProductItem(id: Long) {
        viewModelScope.launch {
            _productOpState.value = ProductCRUDState.Loading
            try {
                when (val result = crudRepository.deleteProductItem(id).last()) {
                    is APIState.Error -> _productOpState.value = ProductCRUDState.Error(result.error, result.statusCode)
                    else -> _productOpState.value = ProductCRUDState.Success("Product deleted successfully!")
                }
            } catch (e: Throwable) {
                _productOpState.value = ProductCRUDState.Error(e.message ?: "Delete failed")
            }
        }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    /**
     * readFileAsBytes
     * Reads a browser File object as a ByteArray using the FileReader API.
     * Suspends until the read is complete or throws on failure.
     *
     * @param file The browser File to read.
     * @return ByteArray of the file's contents.
     */
    private suspend fun readFileAsBytes(file: File): ByteArray = suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onload = {
            val buffer = (reader.result as ArrayBuffer)
            val bytes = Int8Array(buffer).unsafeCast<ByteArray>()
            cont.resume(bytes)
        }
        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read file: ${file.name}"))
        }
        reader.readAsArrayBuffer(file)
    }

    private fun persistTagsAndType(entry: ProductContentFormEntry) {
        viewModelScope.launch {
            entry.tags
                .split("|")
                .map { it.trim().toTitleCase() }
                .filter { it.isNotBlank() }
                .distinct()
                .forEach { tag -> crudRepository.saveTag(tag).collect {} }

            val titleCasedType = entry.type.trim().toTitleCase()
            if (titleCasedType.isNotBlank()) {
                crudRepository.saveType(titleCasedType).collect {}
            }
        }
    }
}

/**
 * ProductContentFormEntry
 * UI-layer form state for a single product content entry (add or edit).
 */
data class ProductContentFormEntry(
    val title: String = "",
    val description: String = "",
    val videoLink: String = "",
    val tags: String = "",
    val type: String = "",
    val thumbnailPath: String = "",
    val thumbnailFile: File? = null,
) {
    /**
     * True when all required fields are present (title is the only required field).
     */
    val isValid: Boolean get() = title.isNotBlank()

    /**
     * Names of required fields that are still empty — used to show validation hints.
     */
    val missingFields: List<String> get() = buildList {
        if (title.isBlank()) add("Title")
    }
}

/**
 * ProductFormEntry
 * UI-layer form state for a single product item (add or edit).
 * All fields are optional except [name].
 */
data class ProductFormEntry(
    val brand: String = "",
    val name: String = "",
    val label: String = "",
    val description: String = "",
    val pros: String = "",
    val cons: String = "",
    val imagePath: String = "",
    val imageFile: File? = null,
    val shopeeLink: String = "",
    val shopeePrice: String = "",
    val shopeePromo: String = "",
    val lazadaLink: String = "",
    val lazadaPrice: String = "",
    val lazadaPromo: String = "",
    val amazonLink: String = "",
    val amazonPrice: String = "",
    val amazonPromo: String = "",
    val tiktokLink: String = "",
    val tiktokPrice: String = "",
    val tiktokPromo: String = "",
) {
    /**
     * True when all backend-required fields are present.
     * brand, name, description are required by POST /products.
     * label has @Column(nullable = false) so it must be non-blank.
     * For add: a new imageFile must be selected.
     */
    val isValid: Boolean get() = imageFile != null &&
            brand.isNotBlank() &&
            name.isNotBlank() &&
            description.isNotBlank() &&
            label.isNotBlank()

    /**
     * isValidForUpdate
     * Same as [isValid] but allows an existing [imagePath] to satisfy the image requirement
     * so the Save button is enabled even when the user has not re-uploaded an image.
     */
    val isValidForUpdate: Boolean get() = (imageFile != null || imagePath.isNotBlank()) &&
            brand.isNotBlank() &&
            name.isNotBlank() &&
            description.isNotBlank() &&
            label.isNotBlank()

    /**
     * Names of required fields that are still empty or missing.
     */
    val missingFields: List<String> get() = buildList {
        if (imageFile == null) add("Image")
        if (brand.isBlank()) add("Brand")
        if (name.isBlank()) add("Name")
        if (description.isBlank()) add("Description")
        if (label.isBlank()) add("Label")
    }
}

/**
 * toFormEntry
 * Convenience extension to pre-populate a [ProductFormEntry] from a [ProductResponse].
 */
fun ProductResponse.toFormEntry() = ProductFormEntry(
    brand        = brand.orEmpty(),
    name         = name.orEmpty(),
    label        = label.orEmpty(),
    description  = description.orEmpty(),
    pros         = pros.orEmpty(),
    cons         = cons.orEmpty(),
    imagePath    = imagePath.orEmpty(),
    shopeeLink   = shopeeLink.orEmpty(),
    shopeePrice  = shopeePrice.orEmpty(),
    shopeePromo  = shopeePromo.orEmpty(),
    lazadaLink   = lazadaLink.orEmpty(),
    lazadaPrice  = lazadaPrice.orEmpty(),
    lazadaPromo  = lazadaPromo.orEmpty(),
    amazonLink   = amazonLink.orEmpty(),
    amazonPrice  = amazonPrice.orEmpty(),
    amazonPromo  = amazonPromo.orEmpty(),
    tiktokLink   = tiktokLink.orEmpty(),
    tiktokPrice  = tiktokPrice.orEmpty(),
    tiktokPromo  = tiktokPromo.orEmpty(),
)
